import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import App from '@app/App';
import { ThemeProvider } from '@app/providers/ThemeProvider';
import '@app/styles/reset.scss';
import '@app/styles/globals.scss';

// 배포 직후 옛 index.html 캐시가 새 chunk 파일명을 못 찾을 때 자동 1회 강제 리로드.
const CHUNK_RELOAD_KEY = 'navido.service.chunk-reload-attempted';
function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  return /Failed to fetch dynamically imported module|Loading chunk .* failed|Importing a module script failed/i.test(msg);
}
window.addEventListener('error', (e) => {
  if (isChunkLoadError(e.error) && !sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    window.location.reload();
  }
});
window.addEventListener('unhandledrejection', (e) => {
  if (isChunkLoadError(e.reason) && !sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    window.location.reload();
  }
});
setTimeout(() => sessionStorage.removeItem(CHUNK_RELOAD_KEY), 10_000);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30_000,
    },
  },
});

// 로그아웃 시 캐시 유출 방지 — useAuth 가 registerQueryCacheCleaner 로 이 함수를 호출.
// 다른 유저 로그인 후 이전 유저의 회차·응답 캐시가 잠깐이라도 노출되는 것을 원천 차단.
import { registerQueryCacheCleaner } from '@shared/lib/auth/useAuth';
registerQueryCacheCleaner(() => queryClient.clear());

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <App />
        </BrowserRouter>
      </ThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </React.StrictMode>
);
